// Warehouse and Location-related filters

import {
	createEntityLookupFilter,
	createKeywordFilter,
	createStatusFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const warehouseFilters: FilterRegistry = {
	warehouseKeywords: {
		...createKeywordFilter({
			label: 'Keywords',
			placeholder: 'Search by name, address, external ID...',
			objectTable: 'warehouse',
			listKeys: ['warehouse'],
			fulltextFields: [
				'name',
				'address1',
				'address2',
				'city',
				'state',
				'zip',
				'externalId',
			],
		}),
		queryParamName: 'warehouseKeywords',
	},

	warehouseCompanies: {
		...createEntityLookupFilter({
			label: 'Company',
			placeholder: 'Search companies...',
			apiEndpoint: '/users/companies',
			objectTable: 'warehouse',
			fieldName: 'fk_warehouse__id',
			listKeys: ['warehouse'],
			displayFields: {
				id: 'id',
				primary: 'label',
			},
			colorScheme: 'purple',
			joinType: 'exists',
			joinTable: 'company_warehouse',
			joinConditions: {
				sourceField: '_id',
				targetField: 'fk_warehouse__id',
				filterField: 'fk_company__id',
			},
		}),
		queryParamName: 'warehouseCompanies',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
	},

	warehouses: {
		type: 'multi-select',
		label: 'Warehouses',
		placeholder: 'Search warehouses...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: '_id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/warehouses',
		queryParamName: 'warehouses',
		multiple: true,
		queryMapping: {
			objectTable: 'warehouse',
			fieldName: '_id',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		listKeys: ['warehouse'],
	},

	locationType: {
		...createStatusFilter({
			label: 'Location Type',
			options: [
				{ id: 'merchandiser', label: 'Merchandiser' },
				{ id: 'warehouse_location', label: 'Warehouse Location' },
				{ id: 'truck', label: 'Truck' },
			],
			objectTable: 'location',
			fieldName: 'type',
			listKeys: ['location'],
			colorScheme: 'purple',
		}),
		queryParamName: 'locationType',
	},

	locationStatus: {
		...createStatusFilter({
			label: 'Location Status',
			options: [
				{ id: 'active', label: 'Active' },
				{ id: 'inactive', label: 'Inactive' },
				{ id: 'full', label: 'Full' },
				{ id: 'maintenance', label: 'Maintenance' },
			],
			objectTable: 'location',
			fieldName: 'status',
			listKeys: ['location'],
			colorScheme: 'green',
		}),
		queryParamName: 'locationStatus',
	},

	// Temperature filter handled by custom server-side logic
	// in locations.service.ts — filters by latest sensor temperature reading vs 40°F threshold
	highTemp: {
		...createStatusFilter({
			label: 'High Temp',
			options: [
				{ id: 'high', label: 'High' },
				{ id: 'low', label: 'Low' },
			],
			objectTable: 'location',
			fieldName: 'highTemp',
			listKeys: ['location'],
			colorScheme: 'red',
		}),
		queryParamName: 'highTemp',
	},

	// Stock level is a computed field handled by custom server-side logic
	// in locations.service.ts (not a direct column filter)
	stockLevel: {
		...createStatusFilter({
			label: 'Stock Level',
			options: [
				{ id: 'empty', label: 'Empty' },
				{ id: 'low', label: 'Low' },
				{ id: 'medium', label: 'Medium' },
				{ id: 'full', label: 'Full' },
			],
			objectTable: 'location',
			fieldName: 'stockLevel',
			listKeys: ['location'],
			colorScheme: 'orange',
		}),
		queryParamName: 'stockLevel',
	},
};
